/**
 * Email Verification Configuration
 *
 * Centralized configuration for email verification links.
 * Ensures all verification emails redirect users back to the app after Firebase applies the verification code.
 */

import { buildAppUrl } from '@/lib/env';
import { logger } from '@/lib/logger';
import type { ActionCodeSettings } from 'firebase/auth';

/**
 * Get action code settings for email verification.
 *
 * This configures Firebase to redirect users to /verify-complete after they click
 * the verification link and Firebase applies the oobCode.
 *
 * Flow:
 * 1. User clicks email link → https://app.modcar.io/__/auth/action?mode=verifyEmail&oobCode=...
 * 2. Firebase's hosted page applies the verification code
 * 3. Firebase redirects to continueUrl → https://app.modcar.io/verify-complete
 * 4. App's /verify-complete page reloads user and routes to next step
 *
 * CONTRACT §9: Cross-environment drift prevention
 * Logs actionCodeSettings.url to detect production/staging config mismatches
 */
export function getEmailVerificationActionCodeSettings(): ActionCodeSettings {
  const continueUrl = buildAppUrl('/verify-complete');

  // CONTRACT §9: Runtime diagnostics for cross-environment drift detection
  // This helps detect if production build is using wrong Firebase config
  if (import.meta.env.DEV || import.meta.env.VITE_APP_ENV !== 'production') {
    logger.info('[EmailVerification] actionCodeSettings.url', { url: continueUrl });
  }

  return {
    url: continueUrl,
    handleCodeInApp: false, // Let Firebase's hosted page handle the oobCode
  };
}
